import React, { useRef } from "react";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
} from "@mui/lab";
import { Typography, Paper, Box } from "@mui/material";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  DandiyaRasIcon,
  GaneshSthapnaIcon,
  GujaratiThaliIcon,
  HastMelapIcon,
  MandapMuratIcon,
  PateBesadvanuIcon,
  VarghodoIcon,
  VelBarotvanuIcon,
} from "./icons";
import "./style.css";

const timelineItems = [
  {
    date: "૨૪ ફેબ્રુઆરી, સોમવારે",
    time: "સવારે ૭:૩૦ વાગ્યે",
    icon: <GaneshSthapnaIcon />,
    title: "ગણેશ સ્થાપના",
    image: "images/ganeshsthapna.png",
    imageAlt: "ગણેશ સ્થાપના",
  },
  {
    date: "૨૪ ફેબ્રુઆરી, સોમવારે",
    time: "સવારે ૮:૦૦ વાગ્યે",
    icon: <PateBesadvanuIcon />,
    title: "પાટે બેસાડવાનું",
    image: "/images/haldi-rasam.png",
    imageAlt: "પાટે બેસાડવાનું",
  },
  {
    date: "૨૪ ફેબ્રુઆરી, સોમવારે",
    time: "સવારે ૮:૩૦ વાગ્યે",
    icon: <VelBarotvanuIcon />,
    title: "વેલ બારોટવાનું",
    image: "/images/velchadavu.png",
    imageAlt: "વેલ બારોટવાનું",
  },
  {
    date: "૨૪ ફેબ્રુઆરી, સોમવારે",
    time: "સાંજે ૭:૩૦ વાગ્યે",
    icon: <DandiyaRasIcon />,
    title: "દાંડિયા રાસ",
    image: "/images/dandiyaras.png",
    imageAlt: "દાંડિયા રાસ",
  },
  {
    date: "૨૫ ફેબ્રુઆરી, મંગળવારે",
    time: "સવારે ૯:૩૦ વાગ્યે",
    icon: <MandapMuratIcon />,
    title: "મંડપ મુહૂર્ત",
    image: "/images/mandapmurat.png",
    imageAlt: "મંડપ મુહૂર્ત",
  },
  {
    date: "૨૫ ફેબ્રુઆરી, મંગળવારે",
    time: "સાંજે ૫:૦૦ વાગ્યે",
    icon: <VarghodoIcon />,
    title: "સામે ચડવાનું",
    image: "/images/varghodo.png",
    imageAlt: "સામે ચડવાનું",
  },
  {
    date: "૨૫ ફેબ્રુઆરી, મંગળવારે",
    time: "સાંજે ૭:૩૦ વાગ્યે",
    icon: <HastMelapIcon />,
    title: "હસ્ત મેળાપ",
    image: "/images/hastmelap.png",
    imageAlt: "હસ્ત મેળાપ",
  },
  {
    date: "૨૫ ફેબ્રુઆરી, મંગળવારે",
    time: "સાંજે ૭:૩૦ વાગ્યે",
    icon: <GujaratiThaliIcon />,
    title: "ભોજન સમારંભ",
    image: "/images/dinner.jpg",
    imageAlt: "ભોજન સમારંભ",
  },
];

const AnimatedTimelineItem = ({ item, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "center center"],
  });

  const opacity = useTransform(scrollYProgress, [0, 0.5], [0, 1]);
  const x = useTransform(scrollYProgress, [0, 0.5], [-50, 0]);

  return (
    <motion.div ref={ref} style={{ opacity, x }}>
      <TimelineItem
        sx={{
          "&:before": {
            display: "none",
          },
        }}
      >
        <TimelineSeparator
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            "& .MuiTimelineConnector-root": {
              backgroundColor: "#EA8FB6", // Custom color for the connector
              width: "2px",
            },
            "& .MuiTimelineDot-root": {
              border: "1px solid #ea8fb6",
              backgroundColor: "white",
            },
          }}
        >
          <TimelineDot
            sx={{
              border: "1px solid #ea8fb6 !important ",
              backgroundColor: "white !important ",
            }}
          >
            {item.icon}
          </TimelineDot>
          <TimelineConnector />
        </TimelineSeparator>
        <TimelineContent sx={{ py: "12px", pl: 5, pr: 0 }}>
          <Paper
            elevation={3}
            sx={{
              position: "relative",
              overflow: "hidden",
              minHeight: "200px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              borderRadius: "10px",
              width: "260px",
            }}
          >
            <img
              src={item.image || "/placeholder.svg"}
              alt={item.imageAlt}
              fill
              height={260}
              width={260}
              style={{ objectFit: "cover", zIndex: "10" }}
            />
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  "linear-gradient(180deg, #e2d1c3 70%,  #ea8fb6 100%)",
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                position: "relative",
                p: 2,
                color: "white",
              }}
            >
              <Typography
                variant="subtitle1"
                component="subtitle1"
                sx={{ opacity: 0.8, color: "black", fontWeight: 500 }}
              >
                {item.date}
              </Typography>
              <Typography
                variant="subtitle2"
                component="subtitle2"
                sx={{ opacity: 0.8, color: "black", fontWeight: 500 }}
              >
                {item.time}
              </Typography>
              <Typography
                variant="h6"
                component="h4"
                sx={{ mt: 1, mb: 1, color: "white", fontWeight: 700 }}
              >
                {item.title}
              </Typography>
              {/* <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {item.description}
              </Typography> */}
            </Box>
          </Paper>
        </TimelineContent>
      </TimelineItem>
    </motion.div>
  );
};

const Timelines = () => {
  return (
    <div id="story" className="story-box main-timeline-box">
      <div className="container">
        <div className="row">
          <div className="col-lg-12">
            <div className="title-box">
              <h1 className="display-4" style={{ padding: "0px" }}>
                શુભ પ્રસંગો
              </h1>
            </div>
          </div>
        </div>

        <Timeline
          sx={{
            alignItems: "center",
          }}
        >
          {timelineItems.map((item, index) => (
            <AnimatedTimelineItem key={index} item={item} index={index} />
          ))}
        </Timeline>
      </div>
    </div>
  );
};

export default Timelines;
